import React, { createContext, useContext, useEffect, useReducer } from 'react';

import * as Sentry from '@sentry/react';

import ScriptLoader from './ScriptLoader';

export enum SCRIPTS {
  VERSE = 'verse',
  MATH_JAX = 'mathJax',
  TEAMS_SHARE = 'teamsShare',
  CHARGEBEE = 'chargebee',
  USER_COM = 'userCom',
}

const VERSE_LOADED_EVENT = 'analyticLibraryLoaded';

const scriptsMetadata = {
  [SCRIPTS.VERSE]: {
    url: process.env.REACT_APP_VERSE_SCRIPT,
    init: () => {
      const videoEvents = window?.videoEvents?.default;
      return new videoEvents(null);
    },
  },
  [SCRIPTS.MATH_JAX]: {
    url: `${process.env.REACT_APP_IMAGEKIT_URL}react-static-files/${process.env.REACT_APP_BUILD_TYPE}/tex-svg-full.js`,
    init: null,
  },
  [SCRIPTS.TEAMS_SHARE]: {
    url: 'https://teams.microsoft.com/share/launcher.js',
    init: null,
  },
  [SCRIPTS.CHARGEBEE]: {
    url: 'https://js.chargebee.com/v2/chargebee.js',
    init: null,
  },
  [SCRIPTS.USER_COM]: {
    url: 'https://xunlocked.user.com/widget.js',
    init: null,
  },
};

function reducer(state, action) {
  switch (action.type) {
    case SCRIPTS.VERSE:
    case SCRIPTS.MATH_JAX:
    case SCRIPTS.TEAMS_SHARE:
    case SCRIPTS.CHARGEBEE:
    case SCRIPTS.USER_COM:
      return { ...state, [action.type]: action.payload };
    default:
      return state;
  }
}

const initialState = {
  [SCRIPTS.VERSE]: null,
  [SCRIPTS.MATH_JAX]: null,
  [SCRIPTS.TEAMS_SHARE]: null,
  [SCRIPTS.CHARGEBEE]: null,
  [SCRIPTS.USER_COM]: null,
};

export const scriptsCtx = createContext<{
  state: typeof initialState;
  dispatch: React.Dispatch<any>;
}>({
  state: initialState,
  dispatch: () => {},
});

export const ScriptsProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { Provider } = scriptsCtx;

  return <Provider value={{ state, dispatch }}>{children}</Provider>;
};

const MOUNTED_SCRIPTS: string[] = [];

function useScriptLoader(type: SCRIPTS) {
  const { state, dispatch } = useContext(scriptsCtx);
  const isLoaded = state[type];

  useEffect(() => {
    const isMounted = MOUNTED_SCRIPTS.includes(type);

    if (!isLoaded && !isMounted && window) {
      // mark as mounted
      MOUNTED_SCRIPTS.push(type);

      const { url, init } = scriptsMetadata[type];
      const loader = new ScriptLoader(type, url, Sentry);

      loader.mount((script) => {
        const dispatchInit = () => dispatch({ type, payload: init?.() || script });

        const verseScriptLoadedHandler = () => {
          dispatchInit();
          window.removeEventListener(VERSE_LOADED_EVENT, verseScriptLoadedHandler);
        };

        if (type === SCRIPTS.VERSE) {
          //use old approach
          if (window?.videoEvents) {
            dispatchInit();
          } else {
            window.addEventListener(VERSE_LOADED_EVENT, verseScriptLoadedHandler);
          }
        } else {
          dispatchInit();
        }
      });
    }
  }, [dispatch, isLoaded, type]);

  return isLoaded;
}

export default useScriptLoader;
